import { type BaseHTMLAttributes, type Ref, useMemo } from 'react';
import { getDateFormatter } from '@interxfi/utilities/date/formatting';
import { usePreferredTimeZone } from '../../../time-zone-context';
import { Typography, type TypographyProps } from '../../typography';

export type WellKnownDateFormatType =
  | 'date_iso'
  | 'date_iso_mm_yyyy'
  | 'date_local'
  | 'date_local_mm_yyyy'
  | 'time_iso'
  | 'time_local'
  | 'time_hh_mm'
  | 'timestamp_iso_ms'
  | 'timestamp_iso_s'
  | 'short_without_year';

const defaultIntlFormatOptionsByWellKnownType: Record<WellKnownDateFormatType, Intl.DateTimeFormatOptions> = {
  date_iso: {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  },
  date_local: {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  },
  date_iso_mm_yyyy: {
    month: '2-digit',
    year: 'numeric',
  },
  date_local_mm_yyyy: {
    month: '2-digit',
    year: 'numeric',
  },
  time_iso: {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZoneName: 'short',
  },
  time_hh_mm: {
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  },
  time_local: {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZoneName: 'short',
  },
  timestamp_iso_ms: {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    fractionalSecondDigits: 3,
    timeZoneName: 'short',
  },
  timestamp_iso_s: {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZoneName: 'short',
  },
  short_without_year: {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  },
};

export interface DateFormatProps extends BaseHTMLAttributes<HTMLTimeElement>, Intl.DateTimeFormatOptions {
  lineBreakDateAndTime?: boolean;
  locale?: string;
  ref?: Ref<HTMLTimeElement>;
  typography?: TypographyProps;
  value?: string | number | Date;
  wellKnownType?: WellKnownDateFormatType;
  withoutTypography?: boolean;
}

export const getDateString = (value: DateFormatProps['value']): string | undefined => {
  if (!value) {
    return undefined;
  }

  try {
    const valueAsDate = new Date(value);

    return valueAsDate.toISOString();
  } catch {
    return undefined;
  }
};

const iso8601Regex = /^\d{4}-\d{2}-\d{2}$/;

/**
 * This function is used to get a time zone-aware ISO 8601 date string for the date input type.
 * Splitting new Date().toISOString() is not time zone-aware.
 */
export function getISO8601Date(
  value: DateFormatProps['value'],
  hourCycle: DateFormatProps['hourCycle'],
  timeZone: DateFormatProps['timeZone'],
  timeZoneName: DateFormatProps['timeZoneName'],
): string | undefined {
  try {
    if (typeof value === 'string' && iso8601Regex.test(value)) {
      return value;
    }

    const dateFromValue = new Date(value || '');
    const parts = getDateFormatter('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hourCycle,
      timeZone,
      timeZoneName,
    }).formatToParts(dateFromValue);

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    if (!year || !month || !day) {
      return '';
    }

    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
}

export function getISO8601MonthYear(
  value: DateFormatProps['value'],
  hourCycle: DateFormatProps['hourCycle'],
  timeZone: DateFormatProps['timeZone'],
  timeZoneName: DateFormatProps['timeZoneName'],
): string | undefined {
  try {
    if (typeof value === 'string' && iso8601Regex.test(value)) {
      const [year, month] = value.split('-');
      return `${month}-${year}`;
    }

    const dateFromValue = new Date(value || '');
    const parts = getDateFormatter('en-US', {
      month: '2-digit',
      year: 'numeric',
      hourCycle,
      timeZone,
      timeZoneName,
    }).formatToParts(dateFromValue);

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;

    if (!year || !month) {
      return '';
    }

    return `${month}-${year}`;
  } catch {
    return '';
  }
}

export const safelyFormatDateValue = (formatter: Intl.DateTimeFormat, value: DateFormatProps['value']): string => {
  if (!value) {
    return '';
  }

  try {
    const dateFromValue = new Date(value);
    return formatter.format(dateFromValue);
  } catch {
    return '';
  }
};

function getDatePortion(
  value: DateFormatProps['value'],
  locale: string,
  dateOptions: Intl.DateTimeFormatOptions,
  wellKnownType: WellKnownDateFormatType,
  hourCycle: DateFormatProps['hourCycle'],
): string {
  switch (wellKnownType) {
    case 'date_iso_mm_yyyy':
      return getISO8601MonthYear(value, hourCycle, dateOptions.timeZone, dateOptions.timeZoneName) || '';
    case 'date_iso':
    case 'timestamp_iso_ms':
    case 'timestamp_iso_s':
      return getISO8601Date(value, hourCycle, dateOptions.timeZone, dateOptions.timeZoneName) || '';
    default:
      return safelyFormatDateValue(getDateFormatter(locale, dateOptions), value)
  }
}

function tryGetUsableLocale(locale: string): string {
  try {
    getDateFormatter(locale, {});
    return locale;
  } catch {
    return 'default';
  }
}

export function getFormattedDateTimeParts({
  dateStyle,
  fractionalSecondDigits,
  hour,
  hourCycle = 'h23',
  minute,
  day,
  month,
  year,
  second,
  value,
  timeZone,
  timeZoneName,
  timeStyle,
  weekday,
  locale = 'default',
  wellKnownType = 'date_iso',
}: DateFormatProps): [string | undefined, string | undefined] {
  const usableLocale = tryGetUsableLocale(locale);
  const hasDatePortion = Boolean(
    day ||
      month ||
      year ||
      dateStyle ||
      ['date_iso', 'date_iso_mm_yyyy', 'date_local', 'date_local_mm_yyyy', 'timestamp_iso_ms', 'timestamp_iso_s', 'short_without_year'].includes(
        wellKnownType,
      ),
  );

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: day ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].day,
    month: month ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].month,
    year: year ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].year,
    dateStyle: dateStyle ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].dateStyle,
    timeZoneName: timeZoneName ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].timeZoneName,
    weekday: weekday ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].weekday,
  };

  if (timeZone) {
    dateOptions.timeZone = timeZone;
  }

  const datePortion = hasDatePortion ? getDatePortion(value, usableLocale, dateOptions, wellKnownType, hourCycle) : undefined;

  const timeOptions = {
    hourCycle: hourCycle ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].hourCycle,
    timeZoneName: timeZoneName ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].timeZoneName,
    hour: hour ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].hour,
    minute: minute ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].minute,
    second: second ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].second,
    fractionalSecondDigits: fractionalSecondDigits ?? defaultIntlFormatOptionsByWellKnownType[wellKnownType].fractionalSecondDigits,
    timeStyle,
    // Need to cast here due to dated TypeScript definitions for Intl.DateTimeFormatOptions
  } as Intl.DateTimeFormatOptions;

  if (timeZone) {
    timeOptions.timeZone = timeZone;
  }

  const timePortion =
    fractionalSecondDigits ||
    hour ||
    minute ||
    second ||
    timeStyle ||
    ['time_iso', 'time_hh_mm', 'time_local', 'timestamp_iso_ms', 'timestamp_iso_s'].includes(wellKnownType)
      ? safelyFormatDateValue(getDateFormatter(usableLocale, timeOptions), value)
      : '';

  return [datePortion, timePortion];
}

export function getFormattedDateTimeString(opts: DateFormatProps): string {
  return getFormattedDateTimeParts(opts).filter(Boolean).join(' ');
}

export function DateFormat({
  dateStyle,
  fractionalSecondDigits,
  hour,
  hourCycle = 'h23',
  minute,
  day,
  month,
  year,
  second,
  value,
  timeStyle,
  timeZone,
  timeZoneName,
  weekday,
  typography,
  lineBreakDateAndTime,
  locale = 'default',
  wellKnownType = 'date_iso',
  withoutTypography,
  ...rest
}: DateFormatProps) {
  const preferredTimeZone = usePreferredTimeZone();
  const usedTimeZone = timeZone || preferredTimeZone;

  const formattedValue = useMemo(
    () =>
      getFormattedDateTimeParts({
        dateStyle,
        fractionalSecondDigits,
        day,
        hour,
        hourCycle,
        wellKnownType,
        locale,
        minute,
        month,
        second,
        timeStyle,
        timeZone: usedTimeZone,
        timeZoneName,
        weekday,
        value,
        year,
      })
        .filter(Boolean)
        .join(lineBreakDateAndTime ? '\n' : ' '),
    [
      dateStyle,
      fractionalSecondDigits,
      day,
      hour,
      hourCycle,
      wellKnownType,
      locale,
      minute,
      month,
      second,
      timeStyle,
      usedTimeZone,
      timeZoneName,
      weekday,
      value,
      year,
      lineBreakDateAndTime,
    ],
  );

  const dateStr = getDateString(value);

  if (withoutTypography) {
    return (
      <time dateTime={dateStr} {...rest}>
        {formattedValue}
      </time>
    );
  }

  return (
    <Typography as="time" dateTime={dateStr} {...typography} {...rest}>
      {formattedValue}
    </Typography>
  );
}
